import { useState, useEffect, useCallback, useMemo } from 'react';
import { saveCartSession, getActiveAndParkedSessions, deleteCartSession } from '../lib/storage';
import type { CartItem, CartSession, Mode } from '../lib/types';

const MAX_SESSIONS = 4; // 1 active + 3 parked

export interface UseCartReturn {
  activeSession: CartSession | null;
  parkedSessions: CartSession[];
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (itemId: string) => void;
  updateItemPrice: (itemId: string, newPrice: number) => void;
  clearCart: () => void;
  completeSession: () => void;
  parkSession: () => void;
  resumeSession: (sessionId: string) => void;
  renameSession: (sessionId: string, label: string) => void;
  startNewSession: (mode: Mode) => void;
  itemCount: number;
  total: number;
  isLoaded: boolean;
}

export function useCart(): UseCartReturn {
  const [activeSession, setActiveSession] = useState<CartSession | null>(null);
  const [parkedSessions, setParkedSessions] = useState<CartSession[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load sessions from IndexedDB on mount
  useEffect(() => {
    async function load() {
      try {
        const sessions = await getActiveAndParkedSessions();
        const active = sessions.find((s) => s.status === 'active') || null;
        const parked = sessions.filter((s) => s.status === 'parked');
        setActiveSession(active);
        setParkedSessions(parked);
      } catch (err) {
        console.error('Failed to load cart sessions:', err);
      } finally {
        setIsLoaded(true);
      }
    }
    load();
  }, []);

  const persist = useCallback(async (session: CartSession) => {
    try {
      await saveCartSession(session);
    } catch (err) {
      console.error('Failed to persist cart session:', err);
    }
  }, []);

  const addItem = useCallback(
    (item: Omit<CartItem, 'id'>) => {
      setActiveSession((prev) => {
        if (!prev) return prev;
        const newItem: CartItem = { ...item, id: crypto.randomUUID() };
        const updated = { ...prev, items: [...prev.items, newItem] };
        persist(updated);
        return updated;
      });
    },
    [persist]
  );

  const removeItem = useCallback(
    (itemId: string) => {
      setActiveSession((prev) => {
        if (!prev) return prev;
        const updated = { ...prev, items: prev.items.filter((i) => i.id !== itemId) };
        persist(updated);
        return updated;
      });
    },
    [persist]
  );

  const updateItemPrice = useCallback(
    (itemId: string, newPrice: number) => {
      setActiveSession((prev) => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          items: prev.items.map((i) =>
            i.id === itemId ? { ...i, finalPrice: newPrice } : i
          ),
        };
        persist(updated);
        return updated;
      });
    },
    [persist]
  );

  const clearCart = useCallback(() => {
    setActiveSession((prev) => {
      if (!prev) return prev;
      deleteCartSession(prev.id).catch(console.error);
      return null;
    });
  }, []);

  const completeSession = useCallback(() => {
    setActiveSession((prev) => {
      if (!prev) return prev;
      const completed: CartSession = {
        ...prev,
        status: 'completed',
        completedAt: new Date().toISOString(),
      };
      persist(completed);
      return null;
    });
  }, [persist]);

  const parkSession = useCallback(() => {
    setActiveSession((prev) => {
      if (!prev) return prev;
      const parked: CartSession = { ...prev, status: 'parked' };
      persist(parked);
      setParkedSessions((ps) => [...ps, parked]);
      return null;
    });
  }, [persist]);

  const resumeSession = useCallback(
    (sessionId: string) => {
      setParkedSessions((prevParked) => {
        const target = prevParked.find((s) => s.id === sessionId);
        if (!target) return prevParked;

        // Park current active session if exists
        setActiveSession((prevActive) => {
          if (prevActive) {
            const parkedActive: CartSession = { ...prevActive, status: 'parked' };
            persist(parkedActive);
            setParkedSessions((ps) => [
              ...ps.filter((s) => s.id !== sessionId),
              parkedActive,
            ]);
          } else {
            setParkedSessions((ps) => ps.filter((s) => s.id !== sessionId));
          }

          const resumed: CartSession = { ...target, status: 'active' };
          persist(resumed);
          return resumed;
        });

        return prevParked; // will be updated by inner setParkedSessions
      });
    },
    [persist]
  );

  const renameSession = useCallback(
    (sessionId: string, label: string) => {
      setActiveSession((prev) => {
        if (prev?.id === sessionId) {
          const updated = { ...prev, label };
          persist(updated);
          return updated;
        }
        return prev;
      });
      setParkedSessions((prev) =>
        prev.map((s) => {
          if (s.id === sessionId) {
            const updated = { ...s, label };
            persist(updated);
            return updated;
          }
          return s;
        })
      );
    },
    [persist]
  );

  const startNewSession = useCallback(
    (mode: Mode) => {
      const totalSessions =
        (activeSession ? 1 : 0) + parkedSessions.length;
      if (totalSessions >= MAX_SESSIONS) return;

      const session: CartSession = {
        id: crypto.randomUUID(),
        mode,
        status: 'active',
        items: [],
        createdAt: new Date().toISOString(),
      };

      // Park current active if exists
      if (activeSession) {
        const parked: CartSession = { ...activeSession, status: 'parked' };
        persist(parked);
        setParkedSessions((ps) => [...ps, parked]);
      }

      persist(session);
      setActiveSession(session);
    },
    [activeSession, parkedSessions.length, persist]
  );

  const itemCount = useMemo(
    () => activeSession?.items.length ?? 0,
    [activeSession]
  );

  const total = useMemo(
    () =>
      activeSession?.items.reduce((sum, item) => sum + item.finalPrice, 0) ?? 0,
    [activeSession]
  );

  return {
    activeSession,
    parkedSessions,
    addItem,
    removeItem,
    updateItemPrice,
    clearCart,
    completeSession,
    parkSession,
    resumeSession,
    renameSession,
    startNewSession,
    itemCount,
    total,
    isLoaded,
  };
}
